import React, { useState, useEffect } from 'react';
import { User, Lock, ChevronUp, ChevronDown, Check, ShieldCheck, Save, Plus, ArrowUpRight, ArrowDownRight, Info, Trash2, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useClientSession } from '@/lib/ClientSessionContext';
import { base44, supabase } from '@/api/base44Client';
import { useClientPOs } from '@/hooks/useClientPOs';
import TrashBin from '@/components/common/TrashBin';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { logAuditEvent } from '@/utils/AuditLogManager';

const fmt = (n) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

// Komponen Simple Bar Chart CSS/SVG (Custom)
const SimpleBarChart = ({ hasOrders = false, chartData = [0, 0, 0, 0, 0, 0, 0] }) => {
  const days = ['Hari 1', 'Hari 2', 'Hari 3', 'Hari 4', 'Hari 5', 'Hari 6', 'Hari 7'];
  const max = Math.max(...chartData, 1.0);

  if (!hasOrders || chartData.every(v => v === 0)) {
    return (
      <div className="w-full h-44 flex flex-col items-center justify-center border border-dashed border-slate-200 rounded-xl bg-slate-50/50 p-6 text-center">
        <p className="text-xs font-bold text-slate-500">Belum Ada Riwayat Order / Transaksi PO</p>
        <p className="text-[11px] text-slate-400 mt-1">Grafik aktivitas akan otomatis terisi saat Anda membuat PO Maklon baru.</p>
      </div>
    );
  }

  return (
    <div className="w-full h-48 flex items-end gap-2 px-4 pt-4 border-l border-b border-slate-200 mt-4 relative">
      <div className="absolute -left-8 bottom-0 text-[10px] text-slate-400">Rp 0</div>
      <div className="absolute -left-10 top-1/2 text-[10px] text-slate-400">Rp {Math.round(max/2)}M</div>
      <div className="absolute -left-10 top-0 text-[10px] text-slate-400">Rp {max}M</div>
      
      {/* Grid lines */}
      <div className="absolute left-0 right-0 top-1/2 border-t border-slate-100 border-dashed w-full -z-10"></div>
      <div className="absolute left-0 right-0 top-0 border-t border-slate-100 border-dashed w-full -z-10"></div>

      {chartData.map((val, i) => (
        <div key={i} className="flex-1 flex flex-col justify-end items-center group relative h-full pb-0.5">
          {val > 0 && (
            <div className="absolute -top-6 bg-slate-800 text-white text-[9px] py-0.5 px-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity">
              Rp {val}M
            </div>
          )}
          <div 
            className="w-full max-w-[24px] bg-[#3E5C0A] hover:bg-[#8C6208] rounded-t-sm transition-all duration-500 cursor-pointer" 
            style={{ height: `${(val / max) * 100}%`, minHeight: val === 0 ? '4px' : '0' }}
          ></div>
          <span className="text-[9px] text-slate-400 absolute -bottom-5 text-center whitespace-nowrap">{days[i]}</span>
        </div>
      ))}
    </div>
  );
};

export default function ClientProfile() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'dashboard'; // 'dashboard' | 'alamat' | 'pengaturan'
  
  const setActiveTab = (tab) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('tab', tab);
      return newParams;
    });
  };

  const [isMenuOpen, setIsMenuOpen] = useState(true);
  const [reportFilter, setReportFilter] = useState('Month');
  const { clientPOs, isLoading: loadingPOs } = useClientPOs();
  const [showTrash, setShowTrash] = useState(false);

  const { clientUser, isLoadingClient, refreshClient } = useClientSession();

  // If loading or no user, handle gracefully or let layout handle
  const fallbackUser = {
    nama: 'PT Client Baru',
    brand_name: 'Brand Baru',
    email: 'email@brand.com',
    pic: 'Admin',
    phone: '08xx',
    status: 'unverified'
  };
  
  const userToUse = clientUser || fallbackUser;

  // State untuk Alamat Kirim (Persisted in localStorage per user)
  const [addresses, setAddresses] = useState(() => {
    try {
      const userKey = clientUser?.id || clientUser?.email || 'guest';
      const saved = localStorage.getItem('satala_client_addresses_' + userKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (e) {}
    return [];
  });
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [newAddr, setNewAddr] = useState({ label: 'Kantor Utama', nama: '', alamat: '', phone: '' });

  // State OTP & Pengaturan Akun
  const [showChangePasswordForm, setShowChangePasswordForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwords, setPasswords] = useState({ old: '', new: '', confirm: '' });
  const [showOtp, setShowOtp] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpAction, setOtpAction] = useState('change_password'); // 'change_password' | 'change_email' | 'view_password'
  const [revealedPassword, setRevealedPassword] = useState('');
  const [brandName, setBrandName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');

  // State Modals Asa
  const [showAsaLinkModal, setShowAsaLinkModal] = useState(false);
  const [showAsaSuccessModal, setShowAsaSuccessModal] = useState(false);
  const [showAsaUnlinkModal, setShowAsaUnlinkModal] = useState(false);

  useEffect(() => {
    if (clientUser?.brand_name) {
      setBrandName(clientUser.brand_name);
    }
    if (clientUser?.phone || clientUser?.whatsapp) {
      setEditPhone(clientUser.phone || clientUser.whatsapp);
    }
    if (clientUser?.email) {
      setEditEmail(clientUser.email);
    } else if (fallbackUser.email) {
      setEditEmail(fallbackUser.email);
    }

    // Sync Addresses from DB ClientAccount / CRM Admin to Client Profile
    if (clientUser) {
      let dbAddresses = [];
      if (Array.isArray(clientUser.addresses) && clientUser.addresses.length > 0) {
        dbAddresses = clientUser.addresses.map((a, idx) => ({
          id: a.id || `addr-${idx}-${Date.now()}`,
          label: a.label || 'Kantor Utama',
          nama: a.nama || a.penerima || clientUser.brand_name || 'Klien',
          alamat: a.alamat || a.alamat_lengkap || '',
          phone: a.phone || a.telepon || clientUser.phone || '',
          isDefault: idx === 0 || a.isDefault === true
        }));
      } else if (clientUser.address_full) {
        dbAddresses = [{
          id: `addr-full-${Date.now()}`,
          label: 'Kantor Utama',
          nama: clientUser.pic || clientUser.owner_name || clientUser.brand_name || 'Klien',
          alamat: clientUser.address_full,
          phone: clientUser.phone || '',
          isDefault: true
        }];
      }

      if (dbAddresses.length > 0) {
        setAddresses(dbAddresses);
        try {
          const userKey = clientUser.id || clientUser.email || 'guest';
          localStorage.setItem('satala_client_addresses_' + userKey, JSON.stringify(dbAddresses));
        } catch (e) {}
      }
    }
  }, [clientUser]);

  // Hitung stats
  const [stats, setStats] = useState({ total: 0, belumBayar: 0, produksi: 0, dikirim: 0, selesai: 0, totalNominal: 0 });
  const [lastVerifReq, setLastVerifReq] = useState(0);

  useEffect(() => {
    if (userToUse.last_verif_req) {
      setLastVerifReq(parseInt(userToUse.last_verif_req, 10));
    }
  }, [userToUse.last_verif_req]);

  // State Modal Konfirmasi Verifikasi Asa
  const [showAsaVerifConfirmModal, setShowAsaVerifConfirmModal] = useState(false);

  // State Modal Hapus Akun
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const handleExecuteDeleteAccount = async () => {
    if (deleteConfirmText.trim().toUpperCase() !== 'HAPUS') {
      toast.error('Ketik HAPUS untuk mengonfirmasi penghapusan.');
      return;
    }

    toast.loading('Menghapus akun Anda secara permanen...', { id: 'delete-acc' });
    try {
      if (clientUser?.id) {
        await base44.entities.ClientAccount.delete(clientUser.id);
      }
      
      logAuditEvent({
        actor: userToUse.brand_name || userToUse.contact_person || 'Client User',
        role: 'Client',
        action: 'Hapus Akun Mandiri',
        target: userToUse.email || 'Client Account',
        ip: '127.0.0.1 (Localhost)',
        browser: 'Chrome 122',
        device: 'Desktop PC (Windows)',
        country: 'Indonesia',
        result: 'Sukses',
        severity: 'CRITICAL',
        details: 'Akun client telah dihapus secara permanen dari sistem oleh pengguna.'
      });

      setShowDeleteAccountModal(false);
      setDeleteConfirmText('');
      toast.success('🗑 Akun Anda telah berhasil dihapus secara permanen.', { id: 'delete-acc' });
      
      if (logoutClient) {
        await logoutClient();
      }
      navigate('/login');
    } catch (e) {
      toast.error('Gagal menghapus akun: ' + e.message, { id: 'delete-acc' });
    }
  };

  const handleAjukanVerifikasi = () => {
    const now = Date.now();
    const oneWeek = 7 * 24 * 60 * 60 * 1000;
    if (now - lastVerifReq < oneWeek) {
      toast.error('Anda baru saja mengajukan verifikasi minggu ini. Silakan tunggu 7 hari lagi sebelum mengajukan kembali.');
      return;
    }
    setShowAsaVerifConfirmModal(true);
  };

  const executeAjukanVerifikasi = async () => {
    setShowAsaVerifConfirmModal(false);
    const now = Date.now();
    setLastVerifReq(now);
    if (clientUser?.id) {
      try {
        await base44.entities.ClientAccount.update(clientUser.id, {
          last_verif_req: now.toString(),
          status: 'unverified'
        });
      } catch (e) {
        console.error("Failed to save verification request time", e);
      }
    }
    toast.success('Pengajuan verifikasi berhasil dikirim! Menunggu pemeriksaan admin backend.');
  };

  useEffect(() => {
    if (!clientUser || loadingPOs) return;

    if (clientPOs) {
      const myPOs = clientPOs.filter(p => 
        p.clientId === clientUser.id || 
        p.customerEmail === clientUser.email ||
        (clientUser.brand_name === 'Cielmora' && p.clientId === 'cli_default')
      );

      let belum = 0, prod = 0, krm = 0, sel = 0, nom = 0;
      myPOs.forEach(p => {
        if (!p.deleted_by_client) {
          if (p.statusBayar === 'BELUM DIBAYAR') belum++;
          else if (p.statusProduksi === 'SELESAI') sel++;
          else if (p.statusProduksi === 'DIKIRIM') krm++;
          else prod++;
          
          nom += (p.shippingCost || 0) + (p.products || []).reduce((s, pr) => s + (pr.total_harga_jual || 0), 0);
        }
      });
      setStats({ total: myPOs.length, belumBayar: belum, produksi: prod, dikirim: krm, selesai: sel, totalNominal: nom });
    }
  }, [clientUser, clientPOs, loadingPOs]);

  const saveAddressesToStorage = async (updated) => {
    setAddresses(updated);
    try {
      const userKey = clientUser?.id || clientUser?.email || 'guest';
      localStorage.setItem('satala_client_addresses_' + userKey, JSON.stringify(updated));
      if (clientUser?.id) {
        await base44.entities.ClientAccount.update(clientUser.id, { addresses: updated });
      }
    } catch (e) {
      console.error("Failed to sync addresses to database", e);
    }
  };

  const handleSaveAddress = (e) => {
    e.preventDefault();
    if (!newAddr.nama || !newAddr.alamat || !newAddr.phone) return;
    const added = { ...newAddr, id: Date.now(), isDefault: addresses.length === 0 };
    const updated = [...addresses, added];
    saveAddressesToStorage(updated);
    setShowAddAddress(false);
    setNewAddr({ label: 'Gudang / Cabang', nama: '', alamat: '', phone: '' });
    toast.success('Alamat kirim baru berhasil disimpan!');
  };

  const handleSaveEditAddress = (e) => {
    e.preventDefault();
    if (!editingAddress || !editingAddress.nama || !editingAddress.alamat) return;
    const updated = addresses.map(a => a.id === editingAddress.id ? editingAddress : a);
    saveAddressesToStorage(updated);
    setEditingAddress(null);
    toast.success('Alamat kirim berhasil diperbarui!');
  };

  const handleDeleteAddress = (id) => {
    const updated = addresses.filter(a => a.id !== id);
    if (updated.length > 0 && !updated.some(a => a.isDefault)) {
      updated[0].isDefault = true;
    }
    saveAddressesToStorage(updated);
    toast.success('Alamat berhasil dihapus!');
  };

  const handleSetDefaultAddress = (id) => {
    const updated = addresses.map(a => ({ ...a, isDefault: a.id === id }));
    saveAddressesToStorage(updated);
    toast.success('Alamat utama berhasil diubah!');
  };

  const requestOtp = async (e) => {
    e.preventDefault();
    
    let isProfileUpdated = false;
    
      if ((brandName && brandName.trim() !== (clientUser?.brand_name || '').trim()) || (editPhone && editPhone.trim() !== (clientUser?.phone || '').trim())) {
        toast.loading('Menyimpan perubahan profil...', { id: 'save-profile' });
        try {
          await base44.entities.ClientAccount.update(clientUser.id, {
            brand_name: brandName ? brandName.trim() : clientUser?.brand_name,
            phone: editPhone ? editPhone.trim() : clientUser?.phone,
            whatsapp: editPhone ? editPhone.trim() : clientUser?.whatsapp,
            status: 'unverified',
            last_verif_req: ''
          });
          await refreshClient();
          toast.success('Profil & Nomor WhatsApp berhasil diperbarui!', { id: 'save-profile' });
          isProfileUpdated = true;
        } catch (err) {
          toast.error('Gagal menyimpan profil: ' + err.message, { id: 'save-profile' });
          return;
        }
      }

      if (!isGoogleLinked && editEmail && editEmail !== (clientUser?.email || '')) {
        setOtpAction('change_email');
        setShowOtp(true);
        return; // Wait for OTP
      }

    if (passwords.new) {
      if (!passwords.old) {
        toast.error('Harap masukkan kata sandi Anda saat ini!');
        return;
      }
      if (passwords.new !== passwords.confirm) {
        toast.error('Konfirmasi kata sandi baru tidak cocok!');
        return;
      }
      if (passwords.new.length < 5) {
        toast.error('Kata sandi baru minimal 5 karakter!');
        return;
      }
      
      toast.loading('Menyimpan kata sandi baru...', { id: 'change-pass' });
      try {
        const { error } = await supabase.auth.updateUser({ password: passwords.new });
        if (error) throw error;
        
        toast.success('Kata sandi berhasil diperbarui!', { id: 'change-pass' });
        setPasswords({ old: '', new: '', confirm: '' });
        setShowChangePasswordForm(false);
        setRevealedPassword('');
      } catch (err) {
        toast.error('Gagal memperbarui kata sandi: ' + err.message, { id: 'change-pass' });
      }
    } else if (!isProfileUpdated) {
      toast.error('Tidak ada perubahan yang dimasukkan.');
    }
  };

  const [isGoogleLinked, setIsGoogleLinked] = useState(false);
  const [googleEmail, setGoogleEmail] = useState('');

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      const isGoogle = 
        user?.app_metadata?.provider === 'google' || 
        user?.app_metadata?.providers?.includes('google') ||
        user?.identities?.some(id => id.provider === 'google') ||
        clientUser?.login_provider === 'google' ||
        clientUser?.auth_provider === 'google' ||
        clientUser?.is_google_linked === true ||
        (clientUser?.email && clientUser.email.includes('gmail.com'));

      if (isGoogle) {
        setIsGoogleLinked(true);
        setGoogleEmail(user?.email || clientUser?.email || '151.store151@gmail.com');
      } else {
        setIsGoogleLinked(false);
        setGoogleEmail('');
      }
    });
  }, [clientUser]);

  const isVerified = userToUse?.status === 'verified';

  const handleSimulateGoogleLink = () => {
    setShowAsaLinkModal(false);
    toast.loading('Menghubungkan ke Google...', { id: 'google-link' });
    setTimeout(() => {
      toast.success('Google terhubung!', { id: 'google-link' });
      setIsGoogleLinked(true);
      setShowAsaSuccessModal(true);
    }, 1500);
  };

  const handleSimulateGoogleUnlink = () => {
    setShowAsaUnlinkModal(false);
    toast.loading('Memutuskan tautan & meminta login baru...', { id: 'google-unlink' });
    setTimeout(() => {
      toast.error('Batal! Anda harus menautkan akun Google baru.', { id: 'google-unlink' });
    }, 2000);
  };

  const verifyOtpAndSave = async () => {
    if (otpCode !== '123456') {
      toast.error('Kode OTP salah!');
      return;
    }
    
    if (otpAction === 'change_email') {
      toast.loading('Memperbarui email...', { id: 'change-email' });
      setTimeout(() => {
        toast.success('Email berhasil diperbarui!', { id: 'change-email' });
        setShowOtp(false);
        setOtpCode('');
      }, 1000);
    } else if (otpAction === 'view_password') {
      toast.success('Verifikasi berhasil! Kata sandi ditampilkan.');
      setRevealedPassword('Satala2026!'); // Sandi simulasi
      setShowOtp(false);
      setOtpCode('');
    } else {
      setShowOtp(false);
      setOtpCode('');
      toast.success('Verifikasi berhasil!');
    }
  };

  return (
    <div className="max-w-6xl mx-auto pb-20">
      <div className="flex flex-col md:flex-row gap-8 items-start">
        
        {/* LEFT COLUMN: NAVIGATION SIDEBAR */}
        <div className="w-full md:w-64 bg-white border border-slate-200 rounded-2xl p-4 shadow-sm shrink-0">
          <div className="space-y-1">
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="w-full flex items-center justify-between px-3 py-2 text-sm font-bold text-slate-800 hover:bg-[#F9F5EC] rounded-xl transition-all">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-[#3E5C0A] flex items-center justify-center shrink-0">
                  <User className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-[#302621]">Profil Klien</span>
              </div>
              {isMenuOpen ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
            </button>
            {isMenuOpen && (
              <div className="pl-6 space-y-1 mt-1 transition-all">
                {[
                  { id: 'dashboard', label: 'Dashboard & Report' },
                  { id: 'alamat', label: 'Daftar Alamat' },
                  { id: 'pengaturan', label: 'Keamanan Akun' }
                ].map(item => (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full text-left px-4 py-2 text-xs font-bold rounded-xl transition-all ${
                      activeTab === item.id ? 'bg-slate-100 text-[#8C6208]' : 'text-[#7A695B] hover:bg-[#F9F5EC] hover:text-[#8C6208]'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
                <button
                  onClick={() => setShowTrash(true)}
                  className="w-full text-left px-4 py-2 text-xs font-bold rounded-xl transition-all text-rose-500 hover:bg-rose-50 hover:text-rose-700 flex items-center gap-2 mt-2"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Keranjang Sampah
                </button>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN: MAIN CONTENT */}
        <div className="flex-1 w-full bg-white border border-slate-200 rounded-3xl p-6 lg:p-8 shadow-sm">
          
          {/* TAB: DASHBOARD REPORT */}
          {activeTab === 'dashboard' && (
            <div className="space-y-6">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h2 className="text-2xl font-black text-[#302621] tracking-tight">{userToUse.brand_name}</h2>
                  <p className="text-sm font-semibold text-[#7A695B] mt-1">{userToUse.nama}</p>
                </div>
                <div className="flex flex-col sm:flex-row items-end sm:items-center gap-3">
                  <div 
                    onClick={() => !isVerified && handleAjukanVerifikasi()}
                    className={`px-3 py-1.5 text-xs font-bold rounded-full border shadow-xs flex items-center gap-1.5 cursor-pointer transition-all ${
                      isVerified ? 'bg-[#E3F9B0] text-[#3E5C0A] border-[#98C81A]' : 'bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200'
                    }`}
                  >
                    {isVerified ? <Check className="w-3.5 h-3.5" /> : <Info className="w-3.5 h-3.5 text-amber-600" />}
                    {isVerified ? 'Terverifikasi' : 'Belum Diverifikasi'}
                  </div>
                  {isVerified ? (
                    <Button onClick={() => navigate('/pesan-po')} className="bg-[#302621] text-white hover:bg-[#1A1411] rounded-full text-xs h-8 px-4 shadow-sm font-bold">
                      Mulai PO Baru
                    </Button>
                  ) : (
                    <Button disabled className="bg-slate-100 text-slate-400 border border-slate-200 rounded-full text-xs h-8 px-4 shadow-xs font-bold cursor-not-allowed">
                      Mulai PO Baru (Terkunci)
                    </Button>
                  )}
                </div>
              </div>

              {!isVerified && (
                <div className="bg-amber-50 border border-amber-200 p-6 rounded-2xl flex flex-col gap-4 mt-4">
                  <div className="flex gap-3">
                    <Info className="w-5 h-5 mt-0.5 shrink-0 text-amber-600" />
                    <p className="text-sm text-amber-900 leading-relaxed font-medium">Akun Anda belum diverifikasi oleh admin pabrik Satala Dermatech. Anda tidak bisa menggunakan fitur <i>Repeat Order</i>.</p>
                  </div>
                  <div>
                    <Button 
                      onClick={handleAjukanVerifikasi} 
                      className={`font-bold h-10 ${Date.now() - lastVerifReq < 7*24*60*60*1000 ? 'bg-slate-200 text-slate-500 cursor-not-allowed hover:bg-slate-200' : 'bg-amber-500 hover:bg-amber-600 text-white shadow-sm'}`}
                    >
                      {Date.now() - lastVerifReq < 7*24*60*60*1000 ? 'Verifikasi Sedang Diproses' : 'Ajukan Verifikasi Akun'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Highlights Block */}
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5">
                <h3 className="text-sm font-bold text-slate-800 mb-4">Highlight <span className="text-xs font-normal text-slate-500 ml-1">Update terbaru pesanan Anda</span></h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-slate-100">
                    <p className="text-2xl font-black text-[#302621]">{stats.belumBayar}</p>
                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mt-1">Belum Dibayar</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-slate-100">
                    <p className="text-2xl font-black text-[#8C6208]">{stats.produksi}</p>
                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mt-1">Produksi</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-slate-100">
                    <p className="text-2xl font-black text-amber-600">{stats.dikirim}</p>
                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mt-1">Dikirim</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-slate-100">
                    <p className="text-2xl font-black text-[#3E5C0A]">{stats.selesai}</p>
                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mt-1">Selesai</p>
                  </div>
                </div>
              </div>

              {/* Quick Report Block */}
              <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
                  <div>
                    <h3 className="text-sm font-bold text-slate-800">Quick Report</h3>
                    <p className="text-[11px] text-slate-500">Ringkasan aktivitas Anda periode ini</p>
                  </div>
                  <div className="flex text-xs bg-slate-50 border border-slate-200 rounded-lg p-1 overflow-hidden">
                    {['Today', 'Last 7 days', 'Week', 'Month'].map(t => (
                      <button 
                        key={t}
                        onClick={() => setReportFilter(t)}
                        className={`px-3 py-1.5 rounded-md font-bold transition-all ${reportFilter === t ? 'bg-white shadow border border-slate-200 text-[#302621]' : 'text-slate-500 hover:text-slate-800'}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  {/* Cards inside Quick Report */}
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-[10px] font-semibold text-slate-500 mb-1 flex items-center gap-1">Total Order <Info className="w-3 h-3 text-slate-300" /></p>
                    <p className="text-sm font-black text-[#302621]">Rp {fmt(stats.totalNominal)}</p>
                    <div className="flex justify-between items-center mt-3 text-[9px] font-bold">
                      <span className="text-slate-400">VS Last month</span>
                      <span className="text-slate-400 flex items-center">{stats.total > 0 ? '+15%' : '0%'}</span>
                    </div>
                  </div>
                  
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-[10px] font-semibold text-slate-500 mb-1 flex items-center gap-1">Kuantitas Produk <Info className="w-3 h-3 text-slate-300" /></p>
                    <p className="text-sm font-black text-[#302621]">{stats.total > 0 ? '20.500 pcs' : '0 pcs'}</p>
                    <div className="flex justify-between items-center mt-3 text-[9px] font-bold">
                      <span className="text-slate-400">VS Last month</span>
                      <span className="text-slate-400 flex items-center">{stats.total > 0 ? '-5%' : '0%'}</span>
                    </div>
                  </div>

                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-[10px] font-semibold text-slate-500 mb-1 flex items-center gap-1">Rata-rata Pembelian <Info className="w-3 h-3 text-slate-300" /></p>
                    <p className="text-sm font-black text-[#302621]">Rp {stats.total > 0 ? fmt(stats.totalNominal / stats.total) : '0'}</p>
                    <div className="flex justify-between items-center mt-3 text-[9px] font-bold">
                      <span className="text-slate-400">VS Last month</span>
                      <span className="text-slate-400 flex items-center">{stats.total > 0 ? '+2%' : '0%'}</span>
                    </div>
                  </div>

                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-[10px] font-semibold text-slate-500 mb-1 flex items-center gap-1">Total Invoice <Info className="w-3 h-3 text-slate-300" /></p>
                    <p className="text-sm font-black text-[#302621]">{stats.total} INV</p>
                    <div className="flex justify-between items-center mt-3 text-[9px] font-bold">
                      <span className="text-slate-400">VS Last month</span>
                      <span className="text-slate-400 flex items-center"> 0%</span>
                    </div>
                  </div>
                </div>

                {/* Custom Chart Area */}
                <div className="bg-slate-50 border border-slate-100 rounded-xl p-5">
                  <div className="flex justify-between items-center mb-6">
                    <h4 className="font-bold text-sm text-[#302621]">Total Order</h4>
                    <Select defaultValue="7_days">
                      <SelectTrigger className="w-[110px] h-7 text-[10px] font-bold bg-white border border-slate-200 rounded focus:ring-0 text-slate-600">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="7_days" className="text-[10px] font-bold cursor-pointer">Last 7 days</SelectItem>
                        <SelectItem value="this_month" className="text-[10px] font-bold cursor-pointer">This Month</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="pt-2 pb-6 pl-2 pr-2">
                    <SimpleBarChart hasOrders={stats.total > 0} />
                  </div>
                </div>

              </div>
            </div>
          )}

          {/* TAB CONTENT: ALAMAT */}
          {activeTab === 'alamat' && (
            <div>
              <div className="flex justify-between items-center border-b border-slate-100 pb-6 mb-6">
                <div>
                  <h2 className="text-2xl font-black text-[#302621] tracking-tight">Daftar Alamat Kirim</h2>
                  <p className="text-xs text-[#7A695B] mt-1">Kelola alamat pengantaran hasil produksi kosmetik Anda.</p>
                </div>
                <button 
                  onClick={() => setShowAddAddress(!showAddAddress)}
                  className="bg-[#302621] text-white hover:bg-slate-800 px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm transition-colors"
                >
                  <Plus className="w-4 h-4" /> Alamat Baru
                </button>
              </div>

              {/* Form Tambah Alamat */}
              {showAddAddress && (
                <form onSubmit={handleSaveAddress} className="bg-[#F9F5EC] border border-[#E6D5B8] p-5 rounded-2xl mb-6 space-y-4">
                  <h4 className="font-bold text-sm text-[#302621]">Tambah Alamat Baru</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] font-bold text-slate-400 block mb-1">Penerima</label>
                      <Input value={newAddr.nama} onChange={e => setNewAddr({...newAddr, nama: e.target.value})} placeholder="Nama Penerima" required />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-slate-400 block mb-1">Nomor Telepon</label>
                      <Input value={newAddr.phone} onChange={e => setNewAddr({...newAddr, phone: e.target.value})} placeholder="Contoh: 08567215745" required />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 block mb-1">Alamat Lengkap</label>
                    <textarea 
                      value={newAddr.alamat} 
                      onChange={e => setNewAddr({...newAddr, alamat: e.target.value})} 
                      className="w-full border border-slate-200 rounded-md p-3 text-xs focus:outline-none focus:ring-2 focus:ring-[#8C6208] h-20 resize-none"
                      required
                    />
                  </div>
                  <div className="flex gap-2 justify-end pt-2">
                    <button type="button" onClick={() => setShowAddAddress(false)} className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-bold text-slate-500 bg-white">Batal</button>
                    <button type="submit" className="px-5 py-2 bg-[#A1C038] hover:bg-[#8CA831] text-white rounded-xl text-xs font-bold shadow-sm">Simpan Alamat</button>
                  </div>
                </form>
              )}

              {/* Form Edit Alamat */}
              {editingAddress && (
                <form onSubmit={handleSaveEditAddress} className="bg-[#F4EFE6] border border-[#A97838] p-5 rounded-2xl mb-6 space-y-4 shadow-sm animate-in fade-in duration-200">
                  <div className="flex justify-between items-center">
                    <h4 className="font-bold text-sm text-[#302621]">Edit Alamat Kirim</h4>
                    <button type="button" onClick={() => setEditingAddress(null)} className="text-xs text-slate-400 hover:text-slate-600 font-bold">Batal</button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-[10px] font-bold text-slate-500 block mb-1">Label Alamat</label>
                      <Input value={editingAddress.label} onChange={e => setEditingAddress({...editingAddress, label: e.target.value})} placeholder="Kantor Utama / Gudang" required />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-slate-500 block mb-1">Nama Penerima / PIC</label>
                      <Input value={editingAddress.nama} onChange={e => setEditingAddress({...editingAddress, nama: e.target.value})} placeholder="Nama Penerima" required />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-slate-500 block mb-1">Nomor WhatsApp / HP</label>
                      <Input value={editingAddress.phone} onChange={e => setEditingAddress({...editingAddress, phone: e.target.value})} placeholder="0812-3456-7890" required />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-500 block mb-1">Alamat Lengkap Pengiriman</label>
                    <textarea 
                      value={editingAddress.alamat} 
                      onChange={e => setEditingAddress({...editingAddress, alamat: e.target.value})} 
                      className="w-full border border-slate-200 rounded-xl p-3 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-[#A97838] h-20 resize-none font-sans"
                      required
                    />
                  </div>
                  <div className="flex gap-2 justify-end pt-1">
                    <button type="button" onClick={() => setEditingAddress(null)} className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-bold text-slate-600 bg-white">Batal</button>
                    <button type="submit" className="px-5 py-2 bg-[#A97838] hover:bg-[#8C6208] text-white rounded-xl text-xs font-bold shadow-sm">Simpan Perubahan Alamat</button>
                  </div>
                </form>
              )}

              {/* Empty State Alamat */}
              {addresses.length === 0 && !showAddAddress && !editingAddress && (
                <div className="border border-dashed border-slate-300 rounded-2xl p-10 text-center bg-slate-50/50 space-y-3 animate-in fade-in duration-200">
                  <div className="w-12 h-12 bg-amber-50 text-[#A97838] rounded-2xl flex items-center justify-center mx-auto border border-amber-200/60 shadow-2xs">
                    <Save className="w-6 h-6 text-[#A97838]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-800 text-sm">Belum Ada Alamat Pengiriman Kirim</h4>
                    <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto leading-relaxed">
                      Daftar alamat pengiriman Anda masih kosong. Tambahkan alamat kantor utama atau gudang untuk mempermudah pengantaran hasil produksi kosmetik.
                    </p>
                  </div>
                  <button 
                    onClick={() => setShowAddAddress(true)}
                    className="bg-[#A97838] hover:bg-[#8C6208] text-white px-5 py-2.5 rounded-xl text-xs font-bold shadow-sm transition-all gap-1.5 inline-flex items-center"
                  >
                    <Plus className="w-4 h-4" /> Tambah Alamat Kirim Pertama
                  </button>
                </div>
              )}

              {/* Daftar Alamat List */}
              <div className="space-y-4">
                {addresses.map(a => (
                  <div key={a.id} className={`border rounded-2xl p-5 shadow-xs relative transition-all ${a.isDefault ? 'border-[#A97838] bg-[#F4EFE6]/40 ring-1 ring-[#A97838]/20' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                    <div className="flex justify-between items-start">
                      <div className="flex gap-2 items-center mb-2">
                        <span className="bg-[#302621] text-white text-[9px] px-2 py-0.5 rounded font-black uppercase">{a.label}</span>
                        {a.isDefault && <span className="bg-emerald-100 text-emerald-800 text-[9px] px-2 py-0.5 rounded font-black uppercase">UTAMA</span>}
                      </div>

                      {/* ACTION BUTTONS: EDIT, DELETE, SET DEFAULT */}
                      <div className="flex items-center gap-3 text-xs">
                        <button 
                          onClick={() => setEditingAddress(a)}
                          className="font-bold text-[#A97838] hover:underline flex items-center gap-1"
                        >
                          ✏ Edit Alamat
                        </button>
                        
                        {!a.isDefault && (
                          <>
                            <button 
                              onClick={() => handleSetDefaultAddress(a.id)} 
                              className="font-bold text-slate-500 hover:text-slate-800 hover:underline"
                            >
                              ⭐ Jadikan Utama
                            </button>

                            <button 
                              onClick={() => handleDeleteAddress(a.id)} 
                              className="font-bold text-rose-500 hover:text-rose-700 hover:underline"
                            >
                              🗑 Hapus
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    <h4 className="font-bold text-[#302621] text-sm mt-1">{a.nama}</h4>
                    <p className="text-xs text-slate-600 mt-1 leading-relaxed">{a.alamat}</p>
                    <p className="text-xs font-bold text-slate-700 mt-2 font-mono">Telp / WA: {a.phone}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB CONTENT: PENGATURAN AKUN / KATA SANDI */}
          {activeTab === 'pengaturan' && (
            <div>
              <div className="border-b border-slate-100 pb-6 mb-6">
                <h2 className="text-2xl font-black text-[#302621] tracking-tight">Keamanan Akun & Profil</h2>
                <p className="text-xs text-[#7A695B] mt-1">Ganti kredensial masuk dan informasi dasar. Nama Brand dikunci oleh sistem pabrik.</p>
              </div>

              {/* Form Ganti Info & Password */}
              <form onSubmit={requestOtp} className="space-y-6">
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-[10px] font-black text-[#8C6208] uppercase tracking-wider block mb-1.5 flex items-center gap-1">
                      Nama Brand Resmi
                    </label>
                    <Input 
                      value={brandName} 
                      onChange={e => setBrandName(e.target.value)} 
                      className="font-bold border-slate-200 focus-visible:ring-[#8C6208]" 
                    />
                    <p className="text-[9px] text-amber-600 mt-1 font-medium">⚠️ Mengubah nama brand akan me-reset verifikasi.</p>
                  </div>

                  <div>
                    <label className="text-[10px] font-black text-[#8C6208] uppercase tracking-wider block mb-1.5">
                      Nomor WhatsApp / HP
                    </label>
                    <Input 
                      placeholder="Contoh: 0812-3456-7890"
                      value={editPhone} 
                      onChange={e => setEditPhone(e.target.value)} 
                      className="font-mono font-bold border-slate-200 focus-visible:ring-[#8C6208]" 
                    />
                    <p className="text-[9px] text-slate-400 mt-1">Digunakan untuk konfirmasi pesanan PO.</p>
                  </div>

                  <div>
                    <label className="text-[10px] font-black text-[#8C6208] uppercase tracking-wider block mb-1.5 flex justify-between">
                      <span>Email Pengguna (Login)</span>
                    </label>
                    <Input 
                      value={editEmail} 
                      onChange={e => setEditEmail(e.target.value)}
                      disabled={isGoogleLinked} 
                      className={`${isGoogleLinked ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : 'bg-white'} font-bold border-slate-200 focus-visible:ring-[#8C6208]`} 
                    />
                    {isGoogleLinked && (
                      <p className="text-[9px] text-slate-400 mt-1">⚠️ Terhubung dengan Google.</p>
                    )}
                  </div>
                </div>

                <hr className="border-slate-100" />

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-[#302621] text-sm">Keamanan Kata Sandi</h3>
                  </div>
                  
                  {!showChangePasswordForm ? (
                    <div className="border border-slate-200 rounded-xl p-4 bg-white shadow-sm flex items-center justify-between">
                      <div>
                        <label className="text-[10px] font-bold text-slate-500 block mb-1">Kata Sandi Saat Ini</label>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm tracking-widest text-[#302621] font-bold">
                            {revealedPassword ? revealedPassword : '••••••••'}
                          </span>
                          {!revealedPassword && (
                            <button type="button" onClick={() => { setOtpAction('view_password'); setShowOtp(true); }} className="text-slate-400 hover:text-[#8C6208] transition-colors p-1" title="Lihat Kata Sandi">
                              <Eye className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      <button type="button" onClick={() => setShowChangePasswordForm(true)} className="bg-slate-100 hover:bg-slate-200 text-[#302621] border border-slate-200 px-4 py-2 rounded-xl text-xs font-bold transition-colors">
                        Ganti Sandi
                      </button>
                    </div>
                  ) : (
                    <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/50 shadow-sm space-y-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-bold text-[#302621] text-xs">Formulir Ganti Kata Sandi</h4>
                        <button type="button" onClick={() => { setShowChangePasswordForm(false); setPasswords({ old: '', new: '', confirm: '' }); }} className="text-[10px] text-slate-500 hover:text-red-500 font-bold underline transition-colors px-2">
                          Batal
                        </button>
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-slate-500 block mb-1">Kata Sandi Saat Ini</label>
                        <div className="relative max-w-sm">
                          <Input 
                            type={showPassword ? "text" : "password"}
                            value={passwords.old} 
                            onChange={e => setPasswords({...passwords, old: e.target.value})} 
                            placeholder="Masukkan sandi Anda saat ini" 
                            className="bg-white pr-10"
                          />
                          <button 
                            type="button" 
                            onClick={() => setShowPassword(!showPassword)} 
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#8C6208]"
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-[10px] font-bold text-slate-500 block mb-1">Kata Sandi Baru</label>
                          <div className="relative">
                            <Input 
                              type={showPassword ? "text" : "password"}
                              value={passwords.new} 
                              onChange={e => setPasswords({...passwords, new: e.target.value})} 
                              placeholder="Min. 5 karakter (huruf/angka)" 
                              className="bg-white pr-10"
                            />
                            <button 
                              type="button" 
                              onClick={() => setShowPassword(!showPassword)} 
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#8C6208]"
                            >
                              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-slate-500 block mb-1">Konfirmasi Sandi Baru</label>
                          <div className="relative">
                            <Input 
                              type={showPassword ? "text" : "password"}
                              value={passwords.confirm} 
                              onChange={e => setPasswords({...passwords, confirm: e.target.value})} 
                              placeholder="Ulangi sandi baru" 
                              className="bg-white pr-10"
                            />
                            <button 
                              type="button" 
                              onClick={() => setShowPassword(!showPassword)} 
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#8C6208]"
                            >
                              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <hr className="border-slate-100" />

                <div className="space-y-4">
                  <h3 className="font-bold text-[#302621] text-sm">Akun Tertaut</h3>
                  <div className="border border-slate-200 rounded-xl p-4 flex items-center justify-between bg-white shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-50 rounded-full flex items-center justify-center border border-slate-100">
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                        </svg>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-bold text-sm text-[#302621]">Google Account</h4>
                          <span className={`text-[9px] font-extrabold px-2 py-0.5 rounded-full ${
                            isGoogleLinked ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-500'
                          }`}>
                            {isGoogleLinked ? '🟢 Terhubung' : 'Belum Terhubung'}
                          </span>
                        </div>
                        <p className="text-xs text-slate-600 mt-0.5 font-medium">
                          {isGoogleLinked 
                            ? `Terhubung dengan email: ${googleEmail || clientUser?.email || '151.store151@gmail.com'}` 
                            : 'Tautkan akun Google Anda untuk akses login 1-klik yang cepat dan praktis.'}
                        </p>
                        {isGoogleLinked && (
                          <p className="text-[10px] text-emerald-700 font-semibold mt-0.5">
                            ✓ Login instan 1-klik aktif tanpa kata sandi.
                          </p>
                        )}
                      </div>
                    </div>
                    {isGoogleLinked ? (
                      <div className="flex items-center gap-3">
                        <button type="button" onClick={() => setShowAsaUnlinkModal(true)} className="text-xs bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-200 px-3 py-1.5 rounded-xl font-bold transition-colors">
                          Putuskan Tautan
                        </button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => setShowAsaLinkModal(true)} className="bg-[#302621] hover:bg-slate-800 text-white px-4 py-2 rounded-xl text-xs font-bold transition-colors shadow-xs">
                        Tautkan Akun Google
                      </button>
                    )}
                  </div>
                </div>

                <div className="pt-2 flex justify-end">
                  <Button type="submit" className="bg-[#302621] hover:bg-slate-800 text-white font-bold px-6 py-2.5 rounded-xl text-xs flex items-center gap-1.5 shadow-sm transition-colors">
                    <Save className="w-4 h-4" /> Simpan Perubahan
                  </Button>
                </div>
              </form>

              {/* ZONA BAHAYA: HAPUS AKUN */}
              <div className="mt-10 pt-6 border-t border-rose-100">
                <div className="bg-rose-50/60 border border-rose-200/90 rounded-2xl p-5 md:p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-rose-100 text-rose-600 rounded-xl border border-rose-200 shrink-0">
                      <Trash2 className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-bold text-rose-900 text-sm">Zona Bahaya: Hapus Akun Klien</h3>
                      <p className="text-xs text-rose-700/80 mt-0.5 font-medium">Penghapusan akun bersifat permanen dan tidak dapat dibatalkan.</p>
                    </div>
                  </div>
                  
                  <p className="text-xs text-slate-600 leading-relaxed">
                    Menghapus akun Anda akan menghapus seluruh data profil, daftar alamat kirim, dan akses ke portal ini secara permanen dari database Satala Dermatech. Anda harus mendaftar ulang jika ingin bermitra kembali.
                  </p>

                  <div className="pt-1 flex justify-end">
                    <Button 
                      type="button" 
                      onClick={() => setShowDeleteAccountModal(true)} 
                      className="bg-rose-600 hover:bg-rose-700 text-white font-bold px-5 py-2 rounded-xl text-xs flex items-center gap-1.5 shadow-xs transition-colors"
                    >
                      <Trash2 className="w-4 h-4" /> Hapus Akun Saya Permanen
                    </Button>
                  </div>
                </div>
              </div>

              {/* OTP Modal */}
              {showOtp && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 -xs animate-in fade-in duration-200">
                  <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl relative text-center">
                    <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <ShieldCheck className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-black text-[#302621] mb-2">Verifikasi Keamanan</h3>
                    <p className="text-xs text-slate-500 mb-6">Kami telah mengirimkan 6 digit OTP ke email Anda. (Gunakan 123456 untuk simulasi ini).</p>
                    <input 
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={otpCode}
                      onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="Masukkan 6 Digit OTP"
                      className="flex h-12 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50 text-center font-mono text-lg tracking-widest mb-4 focus-visible:ring-[#8C6208]"
                      maxLength={6}
                    />
                    <div className="flex gap-2">
                      <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setShowOtp(false)}>Batal</Button>
                      <Button className="flex-1 bg-[#8C6208] hover:bg-[#6b4a04] text-white rounded-xl" onClick={verifyOtpAndSave}>Verifikasi & Simpan</Button>
                    </div>
                  </div>
                </div>
              )}

            </div>
          )}

        </div>
      </div>

      {/* Trash Bin Sidebar */}
      <TrashBin
        isOpen={showTrash}
        onClose={() => setShowTrash(false)}
        title="Keranjang Sampah Saya"
        entities={[
          {
            key: 'ClientPOs',
            label: 'Invoice / PO',
            getDisplayTitle: (item) => `PO #${item.poNumber || item.id} — ${item.customerName || item.brand_name || 'Klien'}`,
          }
        ]}
        customFilter={(item) => {
          if (!clientUser) return false;
          return item.clientId === clientUser.id || item.customerEmail === clientUser.email;
        }}
      />

      {/* POPUP ASA: Link Google */}
      {showAsaLinkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 -xs animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl relative">
            <div className="flex gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex-shrink-0 flex items-center justify-center overflow-hidden border-2 border-amber-200">
                <span className="text-2xl">⚠️</span>
              </div>
              <div>
                <h3 className="font-black text-[#302621] text-lg">Konfirmasi Tautan</h3>
                <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                  "Halo! Pastikan akun Google yang Anda pilih nanti sama persis dengan email terdaftar Anda saat ini. Jika berbeda, email akun Anda akan otomatis diperbarui mengikuti email Google tersebut. Lanjutkan?"
                </p>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button variant="outline" className="flex-1 rounded-xl font-bold" onClick={() => setShowAsaLinkModal(false)}>Batal</Button>
              <Button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold" onClick={handleSimulateGoogleLink}>Lanjut Tautkan</Button>
            </div>
          </div>
        </div>
      )}

      {/* POPUP ASA: Success Link */}
      {showAsaSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 -xs animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl relative text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex-shrink-0 flex items-center justify-center overflow-hidden mx-auto border-4 border-white shadow-sm mb-4">
              <span className="text-3xl">🎉</span>
            </div>
            <h3 className="font-black text-[#302621] text-lg">Tautan Berhasil</h3>
            <p className="text-xs text-slate-600 mt-2 leading-relaxed mb-6">
              "Akun Google Anda telah berhasil ditautkan. Apakah Anda juga ingin memperbarui kata sandi akun Anda sekarang?"
            </p>
            <div className="flex flex-col gap-2">
              <Button className="w-full bg-[#8C6208] hover:bg-[#6b4a04] text-white rounded-xl font-bold" onClick={() => { setShowAsaSuccessModal(false); setShowChangePasswordForm(true); }}>
                Ya, Ganti Sandi
              </Button>
              <Button variant="ghost" className="w-full text-slate-500 font-bold hover:bg-slate-100 rounded-xl" onClick={() => setShowAsaSuccessModal(false)}>
                Nggak Dulu Deh
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* POPUP ASA: Unlink Google */}
      {showAsaUnlinkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 -xs animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl relative">
            <div className="flex gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex-shrink-0 flex items-center justify-center overflow-hidden border-2 border-red-200">
                <span className="text-2xl">🔌</span>
              </div>
              <div>
                <h3 className="font-black text-red-600 text-lg">Putuskan Tautan</h3>
                <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                  "Anda yakin ingin memutuskan tautan Google? Setelah ini, Anda akan diarahkan untuk memilih akun Google baru. Proses putus tautan akan dibatalkan jika Anda tidak menyelesaikan login."
                </p>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button variant="outline" className="flex-1 rounded-xl font-bold" onClick={() => setShowAsaUnlinkModal(false)}>Kembali</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold" onClick={handleSimulateGoogleUnlink}>Lanjut Putuskan</Button>
            </div>
          </div>
        </div>
      )}

      {/* POPUP ASA: Konfirmasi Verifikasi Akun */}
      {showAsaVerifConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 -xs animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl w-full max-w-md p-6 shadow-2xl relative border border-[#E6D5B8] space-y-4 font-sans">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-[#F4EFE6] border-2 border-[#A97838] rounded-2xl flex items-center justify-center shrink-0 shadow-sm overflow-hidden text-2xl">
                🤖
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-black text-[#302621] text-base">Konfirmasi Verifikasi Akun</h3>
                  <span className="text-[10px] font-extrabold bg-[#A97838] text-white px-2 py-0.5 rounded-full">Asa System</span>
                </div>
                <p className="text-xs text-slate-500 font-semibold mt-0.5">Asa AI Assistant • Satala Dermatech</p>
              </div>
            </div>

            <div className="bg-[#FAF8F5] border border-[#ECECEC] p-4 rounded-2xl text-xs text-slate-700 leading-relaxed space-y-2">
              <p className="font-bold text-[#302621]">
                "Hai <span className="text-[#A97838]">{userToUse.brand_name || 'Mitra'}</span>! Asa mau konfirmasi dulu nih... 💡"
              </p>
              <p>
                Kamu beneran mau mengajukan verifikasi akun ke tim admin backend sekarang?
              </p>
              <p className="text-[11px] text-amber-800 bg-amber-50 p-2.5 rounded-xl border border-amber-200 font-medium">
                ⚠️ <b>Fyi ya:</b> Verifikasi ini khusus untuk Mitra Brand yang memang <b>sudah resmi bekerja sama</b> dengan Satala Dermatech. Kalau kamu belum bermitra dengan Satala, tim admin backend <b>tidak akan menyetujui (memverifikasi)</b> akun ini.
              </p>
              <p className="font-medium text-slate-800">
                Mau tetap kirimkan pengajuan verifikasi ini?
              </p>
            </div>

            <div className="flex items-center gap-3 pt-1">
              <Button 
                variant="outline" 
                className="flex-1 rounded-xl font-bold text-xs h-10 border-slate-200 hover:bg-slate-100 text-slate-600" 
                onClick={() => setShowAsaVerifConfirmModal(false)}
              >
                Nanti Dulu
              </Button>
              <Button 
                className="flex-1 bg-[#302621] hover:bg-slate-800 text-white rounded-xl font-bold text-xs h-10 shadow-sm" 
                onClick={executeAjukanVerifikasi}
              >
                Ya, Ajukan Verifikasi
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* POPUP KONFIRMASI HAPUS AKUN */}
      {showDeleteAccountModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 -xs animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl w-full max-w-md p-6 shadow-2xl relative border border-rose-200 space-y-4 font-sans">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-rose-100 text-rose-600 rounded-2xl flex items-center justify-center shrink-0 border border-rose-200 shadow-xs">
                <Trash2 className="w-7 h-7" />
              </div>
              <div>
                <h3 className="font-black text-rose-900 text-base">Hapus Akun Permanen</h3>
                <p className="text-xs text-rose-600 font-semibold mt-0.5">Tindakan ini tidak dapat dibatalkan</p>
              </div>
            </div>

            <div className="bg-rose-50/70 border border-rose-200/80 p-4 rounded-2xl text-xs text-rose-900 leading-relaxed space-y-2">
              <p className="font-bold">
                "Hai <span className="text-rose-700">{userToUse.brand_name || 'Mitra'}</span>! Apakah Anda benar-benar yakin ingin menghapus akun ini secara permanen?"
              </p>
              <p>
                Seluruh profil, data kredensial, dan daftar alamat pengiriman Anda akan dihapus permanen dari server Satala Dermatech.
              </p>
              <div className="pt-2">
                <label className="block text-[11px] font-bold text-slate-700 mb-1">
                  Ketik kata <span className="font-mono text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded font-black">HAPUS</span> untuk konfirmasi:
                </label>
                <Input 
                  value={deleteConfirmText}
                  onChange={e => setDeleteConfirmText(e.target.value)}
                  placeholder='Ketik "HAPUS"'
                  className="bg-white font-mono text-xs font-bold border-rose-300 focus-visible:ring-rose-500"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 pt-1">
              <Button 
                variant="outline" 
                className="flex-1 rounded-xl font-bold text-xs h-10 border-slate-200 hover:bg-slate-100 text-slate-600" 
                onClick={() => { setShowDeleteAccountModal(false); setDeleteConfirmText(''); }}
              >
                Batal
              </Button>
              <Button 
                disabled={deleteConfirmText.trim().toUpperCase() !== 'HAPUS'}
                className="flex-1 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white rounded-xl font-bold text-xs h-10 shadow-sm transition-all" 
                onClick={handleExecuteDeleteAccount}
              >
                Ya, Hapus Permanen
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
